import { LockOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Flex, Form, Input, Typography } from 'antd';
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { useResetPassword } from '../hooks/useAuthQueries';
import { ROUTES } from '../routes/routes';

const { Title } = Typography;

const Wrapper = styled(Flex)`
  min-height: 100vh;
  background: #f4f7f6;
  justify-content: center;
  align-items: center;
`;

const StyledCard = styled(Card)`
  width: 460px;
  border-radius: 16px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
  border: 1px solid #edf2f7 !important;

  .ant-card-body {
    padding: 36px 40px !important;
  }
`;

const LogoMark = styled.div`
  text-align: center;
  margin-bottom: 8px;
  font-size: 24px;
  font-weight: 800;
  color: #ff8e8e;
  letter-spacing: 1px;
`;

const PageTitle = styled(Title)`
  text-align: center !important;
  margin: 0 0 24px !important;
`;

const AlertWrapper = styled.div`
  margin-bottom: 16px;
`;

export const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();
  const { mutate, isPending, isError, error } = useResetPassword();

  const handleSubmit = ({
    newPassword,
    confirmPassword,
  }: {
    newPassword: string;
    confirmPassword: string;
  }) => {
    mutate({ token, newPassword, confirmPassword }, { onSuccess: () => navigate(ROUTES.LOGIN) });
  };

  if (!token) {
    return (
      <Wrapper>
        <StyledCard>
          <Alert type="error" message="Недійсне або відсутнє посилання для скидання пароля." />
        </StyledCard>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <StyledCard>
        <LogoMark>AVON MARKET</LogoMark>
        <PageTitle level={4}>Новий пароль</PageTitle>

        {isError && (
          <AlertWrapper>
            <Alert type="error" message={error?.message ?? 'Помилка скидання пароля'} />
          </AlertWrapper>
        )}

        <Form layout="vertical" onFinish={handleSubmit} autoComplete="off">
          <Form.Item
            name="newPassword"
            rules={[
              { required: true, message: 'Введіть новий пароль' },
              { min: 6, message: 'Пароль має містити щонайменше 6 символів' },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Новий пароль" size="large" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Підтвердіть новий пароль' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Паролі не збігаються'));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Підтвердіть пароль"
              size="large"
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={isPending} block size="large">
              Встановити пароль
            </Button>
          </Form.Item>
        </Form>
      </StyledCard>
    </Wrapper>
  );
};
